import { memo, useMemo } from 'react';
import { clsx } from 'clsx';
import ProductTile from '../../ProductTileView/ProductTile';
import ProductListView from '../../ProductListVIew/ProductListView';
import notEmpty from '../../../../core/util/notEmpty';
import type { ProductWithRelatedData, ProductsViewMode } from '../../../types';
import ProductStocklistView from '../../ProductStocklistView/ProductStocklistView';
import ProductTableView from '../TableView/ProductTableView';
import styles from './ProductListMainContent.module.scss';

const ProductView = memo(
    ({
        viewMode,
        product,
        index,
        variantExistsInList,
    }: {
        product: ProductWithRelatedData;
        index: number;
        variantExistsInList: boolean;
        viewMode: ProductsViewMode;
    }) => {
        const commonProps = {
            renderInstantly: viewMode === 'grid' ? index < 4 : index < 3,
            product: product,
            'data-test-class': 'productTile',
            'data-test-state':
                product.variants.length > 0 ? 'variant' : 'nonVariant',
        };

        switch (viewMode) {
            case 'list': {
                return <ProductListView {...commonProps} />;
            }
            case 'stockList': {
                return <ProductStocklistView {...commonProps} />;
            }
            case 'table': {
                return <ProductTableView {...commonProps} />;
            }
            case 'grid':
            default: {
                return (
                    <ProductTile
                        {...commonProps}
                        stretch
                        variantExistsInList={variantExistsInList}
                    />
                );
            }
        }
    }
);

const ProductListMainContent = ({
    products,
    viewMode,
}: {
    products: ProductWithRelatedData[];
    viewMode: ProductsViewMode;
}) => {
    const variantExistsInList = useMemo(
        () => products.some(item => item.variants.length > 0),
        [products]
    );

    return (
        <div
            className={clsx({
                [styles.productList]: viewMode === 'list',
                [styles.productGrid]: viewMode === 'grid',
                [styles.productStocklist]: viewMode === 'stockList',
            })}
            data-test-class="productItems"
            data-test-state={viewMode}
        >
            {products.filter(notEmpty).map((product, index) => (
                <ProductView
                    viewMode={viewMode}
                    // TODO remove once we get rid of duplicates in Pairs only
                    key={product.id + index}
                    variantExistsInList={variantExistsInList}
                    index={index}
                    product={product}
                />
            ))}
        </div>
    );
};

export default memo(ProductListMainContent);
